import { FC, useEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { FormikSelectSearchField } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { selectUsersState } from "app/data/selectors";
import { getUsersByQueryThunk } from "app/features";
import { selectDrawerSelectedAccountId } from "app/data/selectors";

interface UserFilterProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
}

const UserFilter: FC<UserFilterProps> = ({
  name,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  multiple = false
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { allUsers, loading: usersLoading } = useAppSelector(selectUsersState);

  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      if (!accountId) return;

      setSearchTerm(searchValue);

      const whereConditions = [];

      if (searchValue && searchValue.length >= 2) {
        whereConditions.push({
          field: "user.firstName",
          value: searchValue,
          type: 1
        });
      }

      const searchPayload = {
        accountId,
        params: {
          page: 1,
          itemsPerPage: 50,
          where: whereConditions,
          orderBy: [{ field: "user.firstName", ascending: true }]
        }
      };

      try {
        await dispatch(getUsersByQueryThunk(searchPayload));
      } catch (error) {
        console.error("Error searching users:", error);
      }
    },
    [accountId, dispatch]
  );

  const handleDebouncedSearch = useCallback(
    (searchValue: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        handleSearch(searchValue);
      }, 500);
    },
    [handleSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialLoad && accountId && (!allUsers || allUsers.length === 0)) {
      setIsInitialLoad(false);
      handleSearch("");
    }
  }, [isInitialLoad, accountId, allUsers, handleSearch]);

  const formattedUsers = (allUsers || []).map((user: any) => ({
    ...user,
    displayName: `${user.firstName} ${user.lastName}${user.email ? ` (${user.email})` : ""}`
  }));

  return (
    <FormikSelectSearchField
      name={name}
      fetchOnDebounce={handleDebouncedSearch}
      options={formattedUsers}
      optionLabelKey="displayName"
      optionValueKey="uniqueId"
      loading={usersLoading}
      multiple={multiple}
      searchable
      disabled={disabled}
      placeholder={
        placeholder ||
        t("driverName.placeholder", { ns: "formFieldsTranslation" })
      }
      label={label || t("driverName.label", { ns: "formFieldsTranslation" })}
      fullWidth
      onChange={onChange}
      sx={{
        marginTop: "0 !important",
        marginBottom: "2rem !important",
        "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
          {
            paddingRight: "9px"
          }
      }}
    />
  );
};

export default UserFilter;
